/**
 * Date Normalization Utilities
 *
 * Centralizes ALL date handling for the campaign requestor / queue flow.
 *
 * Philosophy:
 * - Calendar dates (startDate, endDate, flight dates) represent a DAY in the
 *   user's configured timezone. They are stored as UTC moments whose local
 *   calendar day matches the user's intent.
 * - On SAVE:   DatePicker Moment → TZ-aware boundary → UTC Moment → ISO string
 * - On LOAD:   ISO string        → UTC Moment → display via DatePicker
 * - On DISPLAY: Moment object    → wrap in moment(value).tz(timezone) for AntD
 *
 * Never use bare `moment()` or `moment(value)` for form-bound dates — always
 * go through one of the helpers below so timezone handling is explicit.
 */

import moment from 'moment-timezone';

/**
 * Convert a value (ISO string, Date, or Moment) into a UTC Moment representing
 * the START of the calendar day in the given timezone.
 *
 * Use this when LOADING a start date from GraphQL or when normalizing a date
 * for consistent storage.
 *
 * @param {string|Date|moment.Moment|null} value
 * @param {string} timezone  – IANA timezone identifier (e.g. 'America/Los_Angeles')
 * @returns {moment.Moment|null}
 */
export const toStartOfDayUtc = (value, timezone) => {
  if (!value) return null;
  const m = moment.isMoment(value) ? value.clone() : moment.utc(value);
  if (!timezone) return m.startOf('day');
  return moment.tz(m.format('YYYY-MM-DD'), timezone).startOf('day').utc();
};

/**
 * Convert a value into a UTC Moment representing the END of the calendar day
 * in the given timezone.
 *
 * Use this when LOADING an end date from GraphQL.
 *
 * @param {string|Date|moment.Moment|null} value
 * @param {string} timezone
 * @returns {moment.Moment|null}
 */
export const toEndOfDayUtc = (value, timezone) => {
  if (!value) return null;
  const m = moment.isMoment(value) ? value.clone() : moment.utc(value);
  if (!timezone) return m.endOf('day');
  return moment.tz(m.format('YYYY-MM-DD'), timezone).endOf('day').utc();
};

/**
 * Convert a DatePicker onChange value (a Moment in browser local time) into
 * a UTC Moment representing the START of the selected calendar day in the
 * user's configured timezone.
 *
 * Use this in DatePicker onChange handlers for START dates.
 *
 * @param {moment.Moment|null} datePickerValue
 * @param {string} timezone
 * @returns {moment.Moment|null}
 */
export const datePickerToStartOfDayUtc = (datePickerValue, timezone) => {
  if (!datePickerValue) return null;
  if (!timezone) return datePickerValue.clone().utc().startOf('day');
  return moment.tz(datePickerValue.format('YYYY-MM-DD'), timezone).startOf('day').utc();
};

/**
 * Convert a DatePicker onChange value into a UTC Moment representing the END
 * of the selected calendar day in the user's configured timezone.
 *
 * Use this in DatePicker onChange handlers for END dates.
 *
 * @param {moment.Moment|null} datePickerValue
 * @param {string} timezone
 * @returns {moment.Moment|null}
 */
export const datePickerToEndOfDayUtc = (datePickerValue, timezone) => {
  if (!datePickerValue) return null;
  if (!timezone) return datePickerValue.clone().utc().endOf('day');
  return moment.tz(datePickerValue.format('YYYY-MM-DD'), timezone).endOf('day').utc();
};

/**
 * Wrap a UTC Moment (or ISO string) for display in an AntD DatePicker.
 *
 * AntD DatePicker renders in browser local time by default, so we convert the
 * stored UTC value back to the user's timezone so the calendar day matches.
 *
 * @param {string|moment.Moment|null} value
 * @param {string} timezone
 * @returns {moment.Moment|null}
 */
export const toDatePickerDisplay = (value, timezone) => {
  if (!value) return null;
  const m = moment.isMoment(value) ? value.clone() : moment.utc(value);
  if (!timezone) return m.local();
  return m.tz(timezone);
};

/**
 * Create a default start date for a NEW campaign/order/line item:
 * today at start-of-day in the user's timezone, stored as UTC.
 *
 * @param {string} timezone
 * @returns {moment.Moment}
 */
export const getDefaultStartDate = (timezone) => {
  if (!timezone) return moment.utc().startOf('day');
  return moment.tz(timezone).startOf('day').utc();
};

/**
 * Create a default end date for a NEW campaign/order/line item:
 * today + offset days at end-of-day in the user's timezone, stored as UTC.
 *
 * @param {string} timezone
 * @param {number} offsetDays – default 2
 * @returns {moment.Moment}
 */
export const getDefaultEndDate = (timezone, offsetDays = 2) => {
  if (!timezone) return moment.utc().add(offsetDays, 'days').endOf('day');
  return moment.tz(timezone).add(offsetDays, 'days').endOf('day').utc();
};

/**
 * Serialize a form date value for GraphQL mutation.
 *
 * Always produces an ISO-8601 string. Handles Moment objects, strings, Dates.
 *
 * @param {string|Date|moment.Moment|null} value
 * @returns {string|null}
 */
export const serializeDateForGraphQL = (value) => {
  if (!value) return null;
  const m = moment.isMoment(value) ? value.clone() : moment.utc(value);
  return m.toISOString();
};

/**
 * Safe comparison: check if date A is the same calendar day as date B
 * in the given timezone.
 *
 * @param {string|Date|moment.Moment|null} a
 * @param {string|Date|moment.Moment|null} b
 * @param {string} timezone
 * @param {string} granularity – 'day' | 'month' | 'year'
 * @returns {boolean}
 */
export const isSameCalendarDay = (a, b, timezone, granularity = 'day') => {
  if (!a || !b) return false;
  const ma = toDatePickerDisplay(a, timezone);
  const mb = toDatePickerDisplay(b, timezone);
  return ma.isSame(mb, granularity);
};
